/**
 * Brand Colour Validation and Pair Vocabulary
 *
 * The backend accepts `#RRGGBB` and nothing else. The repo's existing
 * `hexToRgb` in `qr-customize/utils/colorValidation` is unsuitable for
 * validation here on two counts: it also accepts three-digit shorthand, and it
 * signals invalidity by throwing. Both matter — shorthand would be a 422, and a
 * throwing validator cannot be called on every keystroke.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.5, 3.6
 */

import type { BrandColorSlot } from '../models/domainModels';

/**
 * The four background slots.
 *
 * Narrower than `BrandColorSlot` so a record of per-pair values can be keyed by
 * background slot and checked for exhaustiveness, rather than carrying a dead
 * entry for each `on*` slot.
 */
export const BACKGROUND_SLOTS = ['accent', 'header', 'surface', 'raised'] as const;

export type BackgroundSlot = (typeof BACKGROUND_SLOTS)[number];

/** A background slot, its foreground slot, and what the pair affects. */
export interface ColorPair {
  readonly background: BackgroundSlot;
  readonly foreground: BrandColorSlot;
  readonly label: string;
  /** What this pair paints in the Go client, in the operator's terms. */
  readonly description: string;
}

/**
 * The four editing groups.
 *
 * Labelled for what they affect in the Go client rather than for the slot
 * names, because an operator choosing a colour is thinking about the header bar
 * or the page background, not about `onSurface`.
 *
 * Every slot appears exactly once: a slot in two groups would be editable in
 * two places, and an omitted slot would be unreachable in the UI while still
 * being sent on every write.
 */
export const COLOR_PAIRS: readonly ColorPair[] = [
  {
    background: 'accent',
    foreground: 'onAccent',
    label: 'Brand Accent',
    description:
      'Primary buttons, toggles and active states. The app derives darker, tint and deep shades from this colour.',
  },
  {
    background: 'header',
    foreground: 'onHeader',
    label: 'Header Bar',
    description: 'The app header and the bay-select bar.',
  },
  {
    background: 'surface',
    foreground: 'onSurface',
    label: 'Page Background',
    description:
      'The page background and its primary text. The text colour also tints stat card text, when it stays legible on white.',
  },
  {
    background: 'raised',
    foreground: 'onRaised',
    label: 'Raised Surfaces',
    description:
      'Bay buttons, dialogs and the shot panel. Stat cards stay white whatever you choose here.',
  },
] as const;

/**
 * A slot's field label: `onAccent` -> `On Accent`.
 *
 * Derived rather than tabulated so a new slot cannot be added without a label.
 */
export const slotLabel = (slot: BrandColorSlot): string => {
  const spaced = slot.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** Exactly `#RRGGBB`. */
const BRAND_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/**
 * Anything that could still become a valid colour with more typing.
 *
 * The hash is optional so a pasted bare hex is not flagged mid-paste.
 */
const PARTIAL_BRAND_COLOR_PATTERN = /^#?[0-9A-Fa-f]{0,6}$/;

/**
 * True for a value the backend will accept.
 *
 * Rejects, non-exhaustively: `#fff`, `#0B5F3AFF`, `0B5F3A`, `rgb(11,95,58)`,
 * `hsl(...)`, `var(--x)`, `red`, `linear-gradient(red,blue)`, and any value with
 * surrounding whitespace.
 */
export const isBrandColor = (value: string): boolean =>
  BRAND_COLOR_PATTERN.test(value);

/**
 * True while a value is still on its way to being valid.
 *
 * Used to decide whether to show a validation error, so that a field does not
 * report an error for every intermediate state of a value being typed.
 */
export const isPartialBrandColor = (value: string): boolean =>
  PARTIAL_BRAND_COLOR_PATTERN.test(value);
